import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PhotoUpload from "@/components/PhotoUpload";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

export default function AddUpdateDialog({ open, onOpenChange, caseId, onUpdated }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!note.trim()) {
      toast({ variant: "destructive", title: "Please add a short note" });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/cases/${caseId}/updates`, { date, note, photos });
      toast({ title: "Update added" });
      setNote("");
      setPhotos([]);
      onUpdated();
      onOpenChange(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Could not add update", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-update-dialog" className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add Treatment Update</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="update-date">Date</Label>
            <Input id="update-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="update-date-input" className="mt-1.5 h-12" />
          </div>
          <div>
            <Label htmlFor="update-note">Note</Label>
            <Textarea id="update-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Wound healing well, more active today" data-testid="update-note-input" className="mt-1.5 min-h-[80px]" />
          </div>
          <div>
            <Label className="mb-2 block">Photos (optional)</Label>
            <PhotoUpload photos={photos} setPhotos={setPhotos} testId="update-photo-upload" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="w-full h-12 bg-amtm-teal text-white hover:bg-amtm-tealHover" data-testid="save-update-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Update"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
